import { gql } from '@apollo/client';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import { t } from 'i18next';
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '~auth/useAuth';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { useAnalyticsReporter } from '~utils/analytics';
import { getId } from '~utils/data';
import { fromError } from '~utils/errors';
import { ensure } from '~utils/types';
import {
  DeletePlaylistsModal_PlaylistFragment,
  useDeletePlaylistsMutation,
} from './__generated__/DeletePlaylistsModal.graphql';

interface Props {
  playlists: DeletePlaylistsModal_PlaylistFragment[];
  isOpen: boolean;
  onCancel: () => void;
  onSuccess?: () => Promise<void> | void;
}

export function DeletePlaylistsModal({ playlists, isOpen, onCancel, onSuccess }: Props) {
  const isPlaylistUsedByDisplays = playlists.some((playlist) => playlist.syncedDisplays.length > 0);
  const isPlaylistUsedByConfigurations = playlists.some(
    (playlist) => playlist.isPlaylistUsedByAnyConfiguration,
  );
  const isPlaylistUsedBySchedules = playlists.some(
    (playlist) => playlist.isPlaylistUsedByAnySchedule,
  );
  const toast = useToast();
  const [deletePlaylists] = useDeletePlaylistsMutation();
  const auth = useAuth();
  const navigate = useNavigate();
  const { customer } = useParams();

  const handleClose = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const handleGoToConfiguration = useCallback(() => {
    onCancel();
    const customerHandle = ensure(customer);
    navigate(`/${auth.organization?.handle}/customers/${customerHandle}/configurations`);
  }, [onCancel, navigate, auth.organization?.handle, customer]);

  const analytics = useAnalyticsReporter();
  const handleDelete = useCallback(async () => {
    try {
      await deletePlaylists({
        variables: {
          input: {
            playlistIds: playlists.map(getId),
          },
        },
      });

      if (playlists.length === 1) {
        analytics.track('playlistDelete');
      } else {
        analytics.track('displayBulkActionComplete', {
          action: 'deletePlaylists',
          displayCount: playlists.length,
        });
      }
      toast({
        status: 'success',
        title: playlists.length === 1 ? t('playlistDeleted') : t('playlistsDeleted'),
        description: playlists.length === 1 ? t('playlistDeleteDesc') : t('playlistDeleteDesc1'),
      });

      await onSuccess?.();
    } catch (err) {
      toast({
        status: 'error',
        title: t('playlistDeleteErr'),
        description: fromError(err, 'DeletePlaylists', {
          PLAYLIST_NOT_FOUND: t('playlistDeleteErrDesc'),
          PLAYLIST_IN_USE_ERROR: t('playlistDeleteErrDesc1'),
        }),
      });
    }
  }, [analytics, deletePlaylists, onSuccess, playlists, toast]);

  const isPlaylistUsedByManyDisplays = playlists.length > 1;

  return (
    <Modal isOpen={isOpen} onClose={onCancel}>
      <ModalOverlay />

      {/* Used in one or more configurations or displays*/}
      {isPlaylistUsedByDisplays || isPlaylistUsedByConfigurations || isPlaylistUsedBySchedules ? (
        <ModalContent>
          <ModalHeader>
            {playlists.length > 1 ? t('deletePlaylistsErr') : t('deletePlaylistErr')}
          </ModalHeader>
          <ModalCloseButton tabIndex={5} />
          <ModalBody>
            <Text>
              {isPlaylistUsedBySchedules
                ? t(
                    isPlaylistUsedByManyDisplays
                      ? 'playlistInUseErrorMany'
                      : 'playlistInUseErrorSingle',
                    {
                      usages: new Intl.ListFormat('en', { type: 'conjunction' }).format(
                        [
                          isPlaylistUsedByDisplays && t('displays').toLowerCase(),
                          isPlaylistUsedBySchedules && t('schedules').toLowerCase(),
                          isPlaylistUsedByConfigurations && t('configurations').toLowerCase(),
                        ].filter((usage): usage is string => !!usage),
                      ),
                    },
                  )
                : isPlaylistUsedByConfigurations && isPlaylistUsedByDisplays
                ? t(
                    isPlaylistUsedByManyDisplays
                      ? 'playlistInUseInConfigAndDisplaysError'
                      : 'playlistInUseInConfigAndDisplaysErrorSingle',
                  )
                : isPlaylistUsedByDisplays
                ? t(
                    isPlaylistUsedByManyDisplays
                      ? 'playlistDeletionErrorMany'
                      : 'playlistDeletionErrorSingle',
                  )
                : t(
                    isPlaylistUsedByManyDisplays
                      ? 'playlistDeletionConfigErrorMany'
                      : 'playlistDeletionConfigErrorSingle',
                  )}
            </Text>
          </ModalBody>

          <ModalFooter>
            {isPlaylistUsedByDisplays || isPlaylistUsedBySchedules ? (
              <Button onClick={handleClose} variant="solid" colorScheme="blue">
                {t('ok')}
              </Button>
            ) : (
              <>
                <Button onClick={handleClose} variant="ghost" colorScheme="blue">
                  {t('cancel')}
                </Button>
                <Button
                  onClick={handleGoToConfiguration}
                  variant="solid"
                  colorScheme="blue"
                  marginLeft="3"
                >
                  {t('goToConfigurations')}
                </Button>
              </>
            )}
          </ModalFooter>
        </ModalContent>
      ) : (
        // Not Used in displays OR configurations
        <ModalContent>
          <ModalHeader>
            {playlists.length > 1 ? t('deletePlaylists') : t('deletePlaylist')}
          </ModalHeader>
          <ModalCloseButton tabIndex={5} />
          <ModalBody>
            <Text>{t('deletePlaylistConfirmation', { count: playlists.length })}</Text>
            <Text color={'gray.600'} marginTop="6">
              {t('actionCannotBeUndone')}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button onClick={handleClose} variant="ghost" colorScheme="blue">
              {t('cancel')}
            </Button>
            <Button onClick={handleDelete} variant="solid" colorScheme="red" marginLeft="3">
              {t('delete')}
            </Button>
          </ModalFooter>
        </ModalContent>
      )}
    </Modal>
  );
}

DeletePlaylistsModal.graphql = {
  fragments: {
    DeletePlaylistsModal_playlist: gql`
      fragment DeletePlaylistsModal_playlist on Playlist {
        id
        syncedDisplays: displays(filter: { state: SYNCED }) {
          id
        }
        isPlaylistUsedByAnyConfiguration
        isPlaylistUsedByAnySchedule
      }
    `,
  },
  mutations: {
    DeletePlaylists: gql`
      mutation DeletePlaylists($input: PlaylistsDeleteInput!) {
        playlistsDelete(input: $input) {
          playlistIds
        }
      }
    `,
  },
};
